import {FileEntity} from 'lib/api/vodInfo';
import {
  VOD_ARCHIVE_KR_CDN_Z01_AFREECATV_DOMAIN,
  AFREECA,
  WOWZA,
} from 'constants/config';
import {TCutInfos, TCutLists} from 'modules/cut';

/**
 * VOD 재생 관련 필요한 함수 모음
 */

export const playbackRateList = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2]; // 재생속도 리스트

/**
 * 현재 seeking했을 때 몇번째 파일인지 찾고, 해당 파일 index 리턴
 * @param files VOD 파일들 담긴 배열
 * @param time 미디어 시간(seconds)
 * @return idx 파일 아이템 인덱스
 */
export const getFileIndexByTime = (files: FileEntity[], time: number) => {
  if (!files) return 0;

  let addTime = 0,
    idx = 0;
  for (let i = 0; i < files.length; i++) {
    addTime += files[i].duration;
    if (addTime >= time) {
      idx = i;
      break;
    }
  }
  return idx;
};

/**
 * 매개변수로 받은 idx까지의 파일 아이템 전체 시간
 * ex) [0,1,2] 파일 배열 중 0->2로 seeking 시에, 1번까지의 총 duration 계산
 * @param files VOD 파일들 담긴 배열
 * @param idx 파일 index
 * @return totalTime 계산한 시간(seconds)
 */
export const getTotalDurationByIndex = (
  files: Array<FileEntity>,
  idx: number,
) => {
  if (idx <= 0) return 0;

  let totalTime = 0;
  for (let i = 0; i < idx; i++) {
    totalTime += files[i].duration;
  }

  return totalTime;
};

/**
 * Video 일시정지 상태값 리턴
 * @param video Video tag element
 * @return Video 일시 정지 여부
 */
export const getPausedState = (video: HTMLVideoElement) => {
  if (!video) return false;

  return video.paused;
};

/**
 * 다음 재생할 파일 아이템 리턴
 * @param currentFileIdx 현재 재생 중인 파일 idx
 * @param files 재생 파일 리스트
 * @return object 다음 파일 아이템
 */
export const getNextFileItem = (
  currentFileIdx: number,
  files: FileEntity[],
) => {
  const nextFileIdx = currentFileIdx + 1;

  let resultObj = {
    fileIdx: -1, // -1은 파일 없는 뜻
    fileInfoKey: '',
    url: '',
    duration: 0,
    fileOrder: 0,
  };

  if (nextFileIdx === files.length) {
    // 다음 재생할 파일 없으면 fileIdx=-1로 리턴
    return resultObj;
  }

  resultObj = {
    fileIdx: files[nextFileIdx].file_idx,
    fileInfoKey: files[nextFileIdx].file_info_key,
    url: files[nextFileIdx].vod_url,
    duration: files[nextFileIdx].duration,
    fileOrder: files[nextFileIdx].file_order,
  };
  return resultObj;
};

/**
 * 다음 컷 편집한 리스트 가져오는 함수
 * @param playingIdx 현재 선택한 컷 편집 리스트
 * @param lists 컷 편집한 리스트들
 */
export const getNextCutFileItem = (playingIdx: number, lists: TCutLists[]) => {
  const nextIdx = playingIdx + 1;
  // const nextIdx = playingIdx;

  let resultObj = {
    idx: -1,
    startTime: 0,
    endTime: 0,
    duration: 0,
  };

  if (nextIdx === lists.length) {
    // 다음 컷 편집 구간 없을 경우, playhead 끝 구간으로 이동시키기 위해 endTime만 셋팅
    resultObj = {
      idx: -1,
      startTime: 0,
      endTime: lists[lists.length - 1].endTime,
      duration: 0,
    };
    return resultObj;
  }

  resultObj = {
    idx: nextIdx,
    startTime: lists[nextIdx].startTime,
    endTime: lists[nextIdx].endTime,
    duration: lists[nextIdx].duration,
  };
  return resultObj;
};

export const getChangeFileItem = (
  video: HTMLVideoElement,
  files: FileEntity[],
  changeFileIdx: number,
) => {
  // 미디어 재생 중이면 파일 교체 위해 잠시 일시 정지
  if (!getPausedState(video)) {
    // video.pause();
  }

  let resultObj = {
    fileIdx: 0,
    fileInfoKey: '',
    url: '',
    duration: 0,
  };
  for (const fileObj of files) {
    const {file_idx, file_info_key, vod_url, duration} = fileObj;

    if (file_idx === changeFileIdx) {
      resultObj = {
        fileIdx: changeFileIdx,
        fileInfoKey: file_info_key,
        url: vod_url,
        duration: duration,
      };
      break;
    }
  }

  return resultObj;
};

export const getElementVideoTag = (): HTMLVideoElement | null => {
  return document.getElementById('video') as HTMLVideoElement;
};

export const setElementVideoTag = () => {
  const element = document.createElement('video');
  element.setAttribute('id', 'video');

  return element;
};

/**
 * 총 파일들 total_duration 기준의 현재 재생 시간
 * @param time 현재 미디어의 재생 시간(seconds)
 */
export const getPlayingTimeByFiles = (
  currentTime: number,
  files: FileEntity[] | TCutLists[],
  currentFileIdx: number,
) => {
  let totalTime = 0;
  for (let i = 0; i < currentFileIdx; i++) {
    totalTime += files[i].duration;
  }
  return totalTime + currentTime;
};

/**
 * 현재 파일 idx까지의 총 duration
 * @param files 파일 리스트
 * @param currentFileIdx 현재 파일 idx
 */
export const getTotalDurationByFiles = (
  files: FileEntity[],
  currentFileIdx: number,
) => {
  let totalTime = 0;
  for (let i = 0; i < currentFileIdx; i++) {
    totalTime += files[i].duration;
  }
  return totalTime;
};

/**
 * 현재 파일 아이템 내인지 아닌지 판단
 * @param idx 파일 index
 */
export const isTimeInCurrentItem = (idx: number, currentFileIdx: number) => {
  return idx === currentFileIdx ? true : false;
};

/**
 * 자체 패킷타이징=afreeca, GSCDN wowza 패킷타이징=wowza
 * @param url hls url
 */
export const getPacketizer = (url: string) => {
  const oURL = new URL(url);

  switch (oURL.hostname) {
    case VOD_ARCHIVE_KR_CDN_Z01_AFREECATV_DOMAIN:
      return AFREECA;
    default:
      return WOWZA;
  }
};

/**
 * Hls 구간 재생 URL 가져오는 함수
 * @param url 재생 Hls URL
 * @param filePath 파일 경로
 * @param sTime 컷 편집 시작 시간
 * @param eTime 컷 편집 종료 시간
 * @param oldEndTime 구 패킷타이징은 wowza 옵션으로 구간 재생하는데, start~duration으로 신규랑 다름
 */
export const getHlsCdnSectionPlayUrl = (
  url: string,
  filePath: string,
  sTime: number,
  eTime: number,
  oldEndTime = -1,
) => {
  const packetizer = getPacketizer(url);
  const oURL = new URL(url);
  const origin = oURL.origin;

  const startMS = Math.floor(sTime * 1000); // ms
  let endMS = Math.floor(eTime * 1000); // ms

  let playURL = '';
  switch (packetizer) {
    case AFREECA: {
      const cFilePath = filePath.replace('/vod/', '/svod/');
      playURL = `${origin}${cFilePath}/clipFrom/${startMS}/clipTo/${endMS}/index.m3u8`;
      break;
    }
    default: {
      // HLS_AFREECATV
      // HLS_TEST_AFREECATV
      const path = filePath.substr(1, filePath.length);
      // playURL = `${url}?wowzaplaystart=${startMS}&wowzaplayduration=${endMS}`;
      // if (oldEndTime !== -1) {
      endMS = Math.floor(oldEndTime * 1000); // ms
      // }
      playURL = `${origin}/video/_definst_/mp4:${path}/playlist.m3u8?wowzaplaystart=${startMS}&wowzaplayduration=${endMS}`;
      break;
    }
  }

  return playURL;
};

/**
 * 편집한 파일 정보 가져옴
 * @param files 편집한 파일 리스트
 * @param playingTime 전체 파일 기준 재생시간
 * @return obj
 */
export const getCutFileInfo = (files: TCutInfos[], playingTime: number) => {
  let obj = {
    fileIdx: -1,
    cutStartTime: 0,
    cutEndTime: 0,
    vodUrl: '',
  };

  for (const file of files) {
    if (playingTime >= file.cutStartTime && playingTime <= file.cutEndTime) {
      obj = file;
      break;
    }
  }
  return obj;
};

export const getCutNearFiles = (files: TCutInfos[], playingTime: number) => {
  let obj = {
    fileIdx: -1,
    cutStartTime: 0,
    cutEndTime: 0,
    vodUrl: '',
  };
  let abs = 0;
  let min = files[files.length - 1].cutStartTime; // 범위 내에 가장 큰 값

  for (const file of files) {
    const time = file.cutStartTime - playingTime;
    abs = time < 0 ? -time : time;

    if (abs <= min) {
      min = abs;
      obj = file;
    }
  }
  return obj;
};

/**
 * 컷 편집한 selected_area 구간 리스트들 가져오는 함수
 * @param lists 컷 편집 리스트
 * @param time 재생 시간
 */
export const getCutListInfo = (
  lists: TCutLists[],
  time: number,
  sectionIdx: number,
) => {
  const obj = {
    idx: 0,
    startTime: 0,
    endTime: 0,
    duration: 0,
    shadeArea: true,
  };

  if (!lists) {
    const tmpObj = {
      ...obj,
      shadeArea: false,
    };
    return tmpObj;
  }

  let isInTime = false;
  for (const list of lists) {
    if (sectionIdx !== list.sectionIdx) continue;

    if (time >= list.startTime && time <= list.endTime) {
      obj.idx = list.idx !== 0 && !list.idx ? -1 : list.idx;
      obj.startTime = list.startTime;
      obj.endTime = list.endTime;
      obj.duration = list.duration;
      obj.shadeArea = false;
      isInTime = true;
      break;
    }
  }

  if (!isInTime) {
    // time이 컷 편집 영역에 없었을 경우
    for (const list of lists) {
      if (time < list.startTime) {
        obj.idx = list.idx as number;
        obj.startTime = list.startTime;
        obj.endTime = list.endTime;
        obj.duration = list.duration;
        obj.shadeArea = true;
        break;
      }
    }
  }

  if (lists.length <= 1 && obj.shadeArea) {
    obj.shadeArea =
      lists[0].idx === 0 &&
      lists[0].startTime === 0 &&
      lists[0].endTime === 0 &&
      lists[0].duration === 0
        ? false
        : obj.shadeArea;
  }

  return obj;
};

/**
 * 장면 내에 편집한 총 시간 가져오는 함수
 * @param array cutLists 편집한 리스트 데이터 배열
 */
export const getCutListTotalDurationInSectcion = (cutLists: TCutLists[]) => {
  if (!cutLists) return 0;

  let totalDuration = 0;
  for (const list of cutLists) {
    totalDuration += list.duration;
  }

  return totalDuration;
};

/**
 * 구간 편집기
 * 전체 그림자 영역 계산
 * @param shades 리덕스 내의 shadeDuration 배열
 */
export const getTotalShadeDuration = (shades: number[]) => {
  let total = 0;

  shades.forEach((val) => (total += val));

  return Number(total.toFixed(2));
};

/**
 * 웹 편집기
 * 전체 편집 영역 계산
 * @param lists 리덕스 내의 cut.lists 배열
 */
export const getTotalEditDuration = (lists: TCutLists[][]) => {
  let totalDuration = 0;
  lists.forEach((aList) => {
    aList.forEach((val) => {
      totalDuration += val.duration;
    });
  });

  return Number(totalDuration.toFixed(2));
};

/**
 * 컷 편집 리스트 중 endTime 가장 큰 값 구하는 함수
 * @param cutInfo 컷 편집 리스트
 * @return max 최대값
 */
export const getMaxEndTimeByCutSection = (cutInfo: TCutLists[]) => {
  let max = 0;
  for (const info of cutInfo) {
    max = Math.max(max, info.endTime);
  }

  return max;
};

/**
 * 타임라인의 마지막 섹션 인지 아닌지 구분하는 함수
 * @param sectionLen 섹션 총 길이
 * @param sectionIdx 섹션 idx
 */
export const isLastTimelineSection = (
  sectionLen: number,
  sectionIdx: number,
) => {
  if (sectionLen <= sectionIdx + 1) {
    return true;
  } else {
    return false;
  }
};

/**
 * 구간 재생 상태 구하는 함수
 * @param fileList 파일 리스트
 * @param cutStartTime  컷 편집 시작 시간
 * @param cutEndTime 컷 편집 끝 시간
 */
export const getSectionPlay = (
  fileList: FileEntity[],
  cutStartTime: number,
  cutEndTime: number,
) => {
  const startFileIdx = getFileIndexByTime(fileList, cutStartTime); // 컷 시작 시간의 파일 인덱스
  const endFileIdx = getFileIndexByTime(fileList, cutEndTime); // 컷 끝 시간의 파일 인덱스
  const totalDurationByStartIdx = getTotalDurationByIndex(
    fileList,
    startFileIdx,
  ); // 컷 시작 시간까지 파일 총 시간(seconds)
  const totalDurationByEndIdx = getTotalDurationByIndex(fileList, endFileIdx); // 컷 끝 시간까지 파일 총 시간(seconds)
  const sectionPlay = [];

  if (startFileIdx < endFileIdx) {
    // 서로 다른 파일이면
    // 컷 시작 먼저
    let sTime = cutStartTime - totalDurationByStartIdx;
    let oldEndTime = fileList[startFileIdx].duration - sTime; // old 패킷타이저(start~duration)
    let eTime = fileList[startFileIdx].duration; // new 패킷타이저(start~end)
    let bStime = false;

    if (sTime !== 0) {
      bStime = true;
      sectionPlay.push({
        cutIdx: 0,
        fileIdx: startFileIdx,
        cutStartTime: Number(sTime.toFixed(2)),
        cutEndTime: Number(eTime.toFixed(2)),
        vodUrl: getHlsCdnSectionPlayUrl(
          fileList[startFileIdx].vod_url,
          fileList[startFileIdx].file_path,
          sTime,
          eTime,
          oldEndTime,
        ),
      });
    }
    // 컷 끝
    sTime = 0;
    eTime = cutEndTime - totalDurationByEndIdx;
    oldEndTime = eTime - sTime;
    sectionPlay.push({
      cutIdx: 0,
      fileIdx: endFileIdx,
      cutStartTime: Number(sTime.toFixed(2)),
      cutEndTime: Number(eTime.toFixed(2)),
      vodUrl: getHlsCdnSectionPlayUrl(
        fileList[endFileIdx].vod_url,
        fileList[endFileIdx].file_path,
        sTime,
        eTime,
        oldEndTime,
      ),
    });
    for (let i = startFileIdx; i <= endFileIdx; i++) {
      // idx가 시작, 끝 idx면 위에서 이미 push했으므로 제외
      if (i === startFileIdx && bStime) {
        continue;
      }
      if (i === endFileIdx) {
        continue;
      }

      const orgObj = {
        cutIdx: 0,
        fileIdx: i,
        cutStartTime: 0,
        cutEndTime: 0,
        vodUrl: fileList[i].vod_url,
      };
      sectionPlay.splice(i, 0, orgObj);
    }
  } else if (startFileIdx === endFileIdx) {
    // 같은 파일 이면
    const sTime = cutStartTime - totalDurationByStartIdx;
    const eTime = cutEndTime - totalDurationByEndIdx;
    const oldEndTime = eTime - sTime;
    sectionPlay.push({
      cutIdx: 0,
      fileIdx: endFileIdx,
      cutStartTime: Number(sTime.toFixed(2)),
      cutEndTime: Number(eTime.toFixed(2)),
      vodUrl: getHlsCdnSectionPlayUrl(
        fileList[endFileIdx].vod_url,
        fileList[endFileIdx].file_path,
        sTime,
        eTime,
        oldEndTime,
      ),
    });
  }

  return sectionPlay;
};

/**
 * 배열 안의 어떤 요소라도 주어진 판별 함수를 통과하는지 체크
 * @param arr 체크할 배열
 * @param val 비교 값
 */
// const checkAvailability = (arr: number[], val: number) => {
//     return arr.some(arrVal => val > arrVal);
// };

// /**
//  * 끝나기 5초 전, 다음 재생 파일 미리 로드
//  * @param currentFile 현재 재생중인 파일
//  * @param currentTime 현재 재생중인 미디어 기준 재생 시간(seconds)
//  * @return boolean true: 있음, false: 없음(미디어 종료)
//  */
// export const isPrepareNextFileItem = (currentFile: TFileState, currentTime: number) => {
//     return (currentFile.duration - currentTime) < 5 ? true : false;
// }
